import {useCallback, useMemo, useState} from 'react'
import type {EditablePreview, IUpdatePreviews, PreviewPayload} from '../types'

interface useEditablePreviews extends IUpdatePreviews {
  editablePreviews: EditablePreview[]
  initializePreviews: (initialPreviews: PreviewPayload) => void
}

const convertPayloadToMap = (previews: PreviewPayload): Map<number, EditablePreview> => {
  const mappedPreviews: Array<[number, EditablePreview]> = Object.entries(previews).map(([id, preview]) => {
    const isDisabled = preview.isFullyCovered || preview.rulesets.length === 0
    return [
      Number(id),
      {
        ...preview,
        id: Number(id),
        isEnabled: preview.isEnabled ?? !isDisabled,
        isDisabled,
      },
    ]
  })
  return new Map(mappedPreviews)
}

export function useEditablePreviews(previews?: PreviewPayload): useEditablePreviews {
  const [editablePreviews, setEditablePreviews] = useState(() => convertPayloadToMap(previews ?? {}))
  const initializePreviews = useCallback(
    (initialPreviews: PreviewPayload) => setEditablePreviews(convertPayloadToMap(initialPreviews)),
    [],
  )

  const togglePreview: useEditablePreviews['togglePreview'] = useCallback(({id}) => {
    setEditablePreviews(currentPreviews => {
      const preview = currentPreviews.get(id)
      if (!preview) {
        return currentPreviews
      }

      const newMap = new Map(currentPreviews)

      newMap.set(id, {
        ...preview,
        isEnabled: !preview.isEnabled,
      })
      return newMap
    })
  }, [])

  const setRulesetName: useEditablePreviews['setRulesetName'] = useCallback(({id, rulesetIndex, newRulesetName}) => {
    setEditablePreviews(currentPreviews => {
      const preview = currentPreviews.get(id)
      if (!preview) {
        return currentPreviews
      }
      const foundRuleset = preview?.rulesets[rulesetIndex]
      if (!foundRuleset) {
        return currentPreviews
      }

      const newMap = new Map(currentPreviews)

      newMap.set(id, {
        ...preview,
        rulesets: [
          ...preview.rulesets.slice(0, rulesetIndex),
          {
            ...foundRuleset,
            name: newRulesetName,
          },
          ...preview.rulesets.slice(rulesetIndex + 1),
        ],
      })
      return newMap
    })
  }, [])

  return useMemo(
    () => ({
      editablePreviews: Array.from(editablePreviews.values()) satisfies EditablePreview[],
      initializePreviews,
      setRulesetName,
      togglePreview,
    }),
    [editablePreviews, initializePreviews, setRulesetName, togglePreview],
  )
}
